/* eslint-disable func-names */
/* eslint-disable guard-for-in */

/**
 * CourseMapD3React — Proof of Concept: Gradual D3 → React migration
 *
 * WHAT CHANGED vs CourseMap:
 *   1. `zoomTransform` state added — updated inside the D3 `zoomed` handler so
 *      React can read and apply the same transform to the overlay SVG.
 *   2-6. All per-node visual elements migrated from D3 imperatives to React
 *      components rendered in the overlay SVG (see overlay comment for list).
 *      D3 retains: zoom/pan, hierarchy layout, link paths, empty <g> node
 *      containers (for collapse/expand transitions), and the defs/clipPath.
 *
 * THE PATTERN (repeat for each subsequent element you want to migrate):
 *   a) Remove the D3 `.append()` calls for that element from useLayoutEffect.
 *   b) Create a React component that renders the equivalent SVG element.
 *   c) Place it inside the overlay <g> using `node.x` / `node.y` from courseCoordsArray.
 *   d) The overlay SVG's transform keeps it pixel-perfect over the D3 canvas.
 *
 * KNOWN TRADEOFF:
 *   `setZoomTransform` is called on every zoom/pan event, triggering a React
 *   re-render. For production, memoize the overlay content with React.memo and
 *   use useDeferredValue on the transform so panning stays smooth.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as d3 from 'd3';
import { HelpCircle, History, PanelRight, Redo2, Undo2 } from 'lucide-react';
import type React from 'react';
import {
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';
import AddCoursePopover from '@/components/CourseMap/AddCoursePopover';
import AdminCourseMapIntro from '@/components/CourseMap/AdminCourseMapIntro';
import CourseMapList from '@/components/CourseMap/CourseMapList/CourseMapList';
import {
	StyledMap,
	StyledMapToolbar,
	StyledUnsavedButton,
} from '@/components/CourseMap/CourseMapStyles';
import {
	adjustDisciplineTextPosition,
	click,
	collapseTree,
	collectAllHierarchyNodes,
	createCourseMapPaths,
	createPanLimits,
	diagonal,
	isEmpty,
	isSpacerMapNode,
	rectNode,
	wrapTextElement,
} from '@/components/CourseMap/course-utils';
import type {
	CourseMapRoot,
	CourseNodeBase,
	FormSubmit,
	MapMargin,
	NodeEntity,
} from '@/components/CourseMap/coursemap.types';
import MapButtonControls from '@/components/CourseMap/MapButtonControls';
import MergePopover from '@/components/CourseMap/MergePopover';
import Loader from '@/components/Loader';
import {
	showErrorToast,
	showSuccessToast,
	ToastNotification,
} from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import {
	Drawer,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer';
import GlobalContext from '@/context/GlobalContext';
import { deleteCourse, getCourseMap, updateCourseMap } from '@/hooks/queries/useCourseMap';
import type { CourseWithRelations } from '@/lib/types';
import { cn } from '@/lib/utils';
import useCourseMapStore, { type HistoryEntry } from '@/stores/courseMapStore';

// ─── REACT-RENDERED SVG COMPONENT ────────────────────────────────────────────
// This is the first element migrated from D3 imperatives to a React component.
// Previously rendered by two D3 `.append()` chains in the update() function.

interface ViewButtonProps {
	node: NodeEntity;
	passedEvent?: (id: string) => void;
}

const ViewButton = ({ node, passedEvent }: ViewButtonProps) => (
	<g
		style={{
			transform: `translate(${node.y}px, ${node.x - rectNode.height / 2}px)`,
			transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
		}}
	>
		{/* biome-ignore lint/a11y/noStaticElementInteractions: this is a view button */}
		<rect
			width={65}
			height={30}
			fill="var(--org-color)"
			x={176}
			y={73}
			rx={6}
			className="view-button"
			style={{ cursor: 'pointer', pointerEvents: 'auto' }}
			onClick={() => passedEvent?.(node.data.tracker)}
		/>
		<text
			className="view-button-text"
			y={93}
			x={189}
			fill="white"
			style={{
				fontSize: '16px',
				textShadow: '-2px 2px 6px rgba(0,0,0,.7)',
				pointerEvents: 'none',
			}}
		>
			View
		</text>
	</g>
);

// ── Shared utility (used by NodeReorderControls and the move handlers) ────────
function moveChildrenArray(arr: unknown[], fromIndex: number, toIndex: number) {
	const element = arr[fromIndex];
	arr.splice(fromIndex, 1);
	arr.splice(toIndex, 0, element);
}

// ── Migration 2: NodeAddRemoveButton ─────────────────────────────────────────
// Replaces the D3-imperative editIcon circle + plus/minus path icon.
// The `activeTrackerId` prop drives the `active-icon` CSS pulse class so we no
// longer need `d3.select(...).classed('active-icon', true)` anywhere.
const PLUS_ICON_PATH = 'M7.5 6.5H14v1H7.5V14h-1V7.5H0v-1h6.5V0h1z';
const MINUS_ICON_PATH = 'M14 .5v1H0v-1z';

interface NodeAddRemoveButtonProps {
	node: NodeEntity;
	isAddingCourse: boolean;
	activeTrackerId: string | null;
	onAddClick: (node: NodeEntity) => void;
	onRemoveClick: (node: NodeEntity) => void;
}

const NodeAddRemoveButton = ({
	node,
	isAddingCourse,
	activeTrackerId,
	onAddClick,
	onRemoveClick,
}: NodeAddRemoveButtonProps) => {
	const isRoot = node.depth === 0;
	const isDisciplineWithChildren =
		node.depth === 1 && node.data.isDiscipline && (node.children?.length ?? 0) > 0;
	const isActive = activeTrackerId === node.data.tracker;

	const circleClass = [
		isAddingCourse
			? `map-plus-icon plus-icon-${node.data.tracker}-${node.depth}`
			: isRoot || isDisciplineWithChildren
				? 'base-node-icon'
				: 'map-minus-icon',
		isActive ? 'active-icon' : '',
	]
		.filter(Boolean)
		.join(' ');

	const showIconPath = !isRoot && !isDisciplineWithChildren;
	const yPos = isAddingCourse ? rectNode.height / 2 - 6.5 : rectNode.height / 2 - 0.5;

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (isAddingCourse) onAddClick(node);
		else if (!isRoot) onRemoveClick(node);
	};

	return (
		<g
			style={{
				transform: `translate(${node.y}px, ${node.x - rectNode.height / 2}px)`,
				transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
			}}
		>
			{/* biome-ignore lint/a11y/noStaticElementInteractions: this is a add/remove button */}
			<circle
				r={13}
				cx={rectNode.extWidth}
				cy={rectNode.height / 2}
				className={circleClass}
				style={{ cursor: 'pointer', pointerEvents: 'auto' }}
				onClick={handleClick}
			/>
			{showIconPath && (
				<path
					stroke="white"
					fill="white"
					transform={`translate(${rectNode.width + 43},${yPos})`}
					style={{ pointerEvents: 'none' }}
					d={isAddingCourse ? PLUS_ICON_PATH : MINUS_ICON_PATH}
				/>
			)}
		</g>
	);
};

// ── Migration 3: NodeReorderControls ─────────────────────────────────────────
// Replaces the D3-imperative move-up / move-down circles and chevron paths
// that were appended only for depth-1 discipline nodes.
const ARROW_DOWN_PATH =
	'M12 16a1 1 0 0 1-.7-.3l-6-6a1 1 0 0 1 1.4-1.4l5.3 5.3 5.3-5.3a1 1 0 0 1 1.4 1.4l-6 6a1 1 0 0 1-.7.3z';
const ARROW_UP_PATH =
	'M18 16a1 1 0 0 1-.7-.3L12 10.4l-5.3 5.3a1 1 0 0 1-1.4-1.4l6-6a1 1 0 0 1 1.4 0l6 6A1 1 0 0 1 18 16z';

interface NodeReorderControlsProps {
	node: NodeEntity;
	onMoveUp: (node: NodeEntity) => void;
	onMoveDown: (node: NodeEntity) => void;
}

const NodeReorderControls = ({ node, onMoveUp, onMoveDown }: NodeReorderControlsProps) => {
	if (node.depth !== 1) return null;
	return (
		<g
			style={{
				transform: `translate(${node.y}px, ${node.x - rectNode.height / 2}px)`,
				transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
			}}
		>
			{/* biome-ignore lint/a11y/noStaticElementInteractions: this is a up/down button */}
			<circle
				r={13}
				cx={-10}
				cy={rectNode.height / 2 - 20}
				fill="var(--neutral-500)"
				style={{ cursor: 'pointer', pointerEvents: 'auto' }}
				onClick={(e) => {
					e.stopPropagation();
					onMoveUp(node);
				}}
			/>
			{/* biome-ignore lint/a11y/noStaticElementInteractions: this is a down button */}
			<circle
				r={13}
				cx={-10}
				cy={rectNode.height / 2 + 20}
				fill="var(--neutral-500)"
				style={{ cursor: 'pointer', pointerEvents: 'auto' }}
				onClick={(e) => {
					e.stopPropagation();
					onMoveDown(node);
				}}
			/>
			<path
				stroke="white"
				fill="white"
				transform="translate(-22,64.5)"
				style={{ pointerEvents: 'none' }}
				d={ARROW_DOWN_PATH}
			/>
			<path
				stroke="white"
				fill="white"
				transform="translate(-22,22.5)"
				style={{ pointerEvents: 'none' }}
				d={ARROW_UP_PATH}
			/>
		</g>
	);
};
// ── Migration 4: NodeMergeButton ──────────────────────────────────────────────
// Replaces the D3-imperative mergeIcon circle + M/R glyph path.
// Computes the same visibility/class logic that was in the D3 `.attr('class')`
// callback so the CSS animations (e.g. map-merge-icon pulse) still fire.
const MERGE_GLYPH_M = 'M1 .5L4.2 8 7.3.5h1V9h-.7V1.5L4.5 9h-.6L.7 1.5l.1 3.8V9H.1V.5h1z';
const MERGE_GLYPH_R =
	'M3.3 5.4H.8V9H0V.5h2.7c1 0 1.7.2 2.2.6.5.5.7 1 .7 1.9 0 .5-.1 1-.4 1.4-.4.4-.8.7-1.3.9L6 8.9V9h-.8l-2-3.6zM.8 4.8H3c.6 0 1-.1 1.5-.5.3-.3.5-.8.5-1.3 0-.6-.2-1-.6-1.4C4 1.2 3.5 1 2.8 1h-2v3.7zM8.7.5L11.8 8 15 .5h1V9h-.8V1.5L12.2 9h-.6L8.4 1.5V9h-.7V.5h1z';

interface NodeMergeButtonProps {
	node: NodeEntity;
	isAddingCourse: boolean;
	onMergeClick: (node: NodeEntity) => void;
}

const NodeMergeButton = ({ node, isAddingCourse, onMergeClick }: NodeMergeButtonProps) => {
	const isMergeValid = node
		.ancestors()
		.filter(
			(c) =>
				c.data.discipline === node.data.discipline &&
				c.depth >= 1 &&
				(c.children?.length ?? 0) >= 1 &&
				c.data.tracker !== node.data.tracker
		)
		.some((c) => (c.children?.length ?? 0) > 1);

	const hasMultiChildren = node.ancestors().filter((a) => (a.children?.length ?? 0) >= 2);
	const isOrphan = hasMultiChildren[0]?.depth === 0;

	const circleClass = (() => {
		if (isAddingCourse && isMergeValid)
			return `map-merge-icon merge-icon-${node.data.tracker}-${node.depth}`;
		if (isOrphan || node.parent?.parent?.children?.length === 1) return 'empty-icon';
		if (node.depth === 0) return 'base-node-icon';
		if (node.data.suppParent && node.data.suppParent?.length !== 0) return 'map-minus-icon';
		return 'empty-icon';
	})();

	const glyphPath = (() => {
		if (isAddingCourse && node.depth !== 0 && !isOrphan) return MERGE_GLYPH_M;
		if (node.data.suppParent && node.data.suppParent?.length !== 0) return MERGE_GLYPH_R;
		return null;
	})();

	const glyphX = isAddingCourse ? -4.5 : -7.5;

	return (
		<g
			style={{
				transform: `translate(${node.y}px, ${node.x - rectNode.height / 2}px)`,
				transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
			}}
		>
			{/* biome-ignore lint/a11y/noStaticElementInteractions: this is a merge button */}
			<circle
				r={13}
				cx={0}
				cy={0}
				className={circleClass}
				style={{ cursor: 'pointer', pointerEvents: 'auto' }}
				onClick={(e) => {
					e.stopPropagation();
					onMergeClick(node);
				}}
			/>
			{glyphPath && (
				<path
					stroke="white"
					fill="white"
					transform={`translate(${glyphX},-4.5)`}
					style={{ pointerEvents: 'none' }}
					d={glyphPath}
				/>
			)}
		</g>
	);
};
// ── Migration 5: NodeCard ─────────────────────────────────────────────────────
// Replaces all of the D3 nodeEnter visual appends:
//   backdrop rect, main card rect, discipline image, overlay path,
//   colour-bar path (depth 1), text-bg path (depth > 1),
//   division dot + label (depth > 1), and the wrapped course-name text.
//
// TEXT WRAPPING NOTE: wrapTextElement() and adjustDisciplineTextPosition() from
// course-utils-react.ts measure the DOM via getComputedTextLength() /
// getClientRects(). They are called via a useRef + useEffect after the <text>
// mounts. Unlike the original D3 helpers, they accept node metadata as explicit
// args instead of reading from a D3 datum (which React elements don't have).
//
// ANIMATION TRADE-OFF: D3 still animates the (now-empty) <g> containers to
// their final positions. NodeCard elements jump directly to final coords from
// courseCoordsArray. For admin mode (transitions disabled) this is invisible.
// For non-admin, enter/exit transitions on individual cards are lost; the
// zoom/pan and collapse animations are unaffected.

// Card geometry constants — derived from rectNode so they stay consistent.
const CURVE = 6;
const WIDTH_EXT_WITH_CURVE = rectNode.extWidth - CURVE * 2; // 238
const WIDTH_WITH_CURVE = rectNode.width - CURVE; // 194
const COLOR_BAR_WIDTH = 30;
const TEXT_BG_WIDTH = rectNode.extWidth - rectNode.width - CURVE; // 44
const CENTER_POINT = rectNode.extWidth - rectNode.width; // 50
const DOT_RADIUS = 14;
const DIVISION_TEXT_SIZE = 28;
const IMAGE_HEIGHT_ROOT = rectNode.height - 20; // 90

interface NodeCardProps {
	node: NodeEntity;
	disciplineData: { name: string; image?: string | null }[] | undefined;
	siteSettings: { logo: string | null } | undefined;
	/** When set, the card animates in from this position on mount (expand). */
	enterFrom?: { x: number; y: number };
	onClick?: () => void;
	onPointerDown?: (e: React.PointerEvent<SVGGElement>) => void;
	onPointerMove?: (e: React.PointerEvent<SVGGElement>) => void;
	onPointerUp?: (e: React.PointerEvent<SVGGElement>) => void;
	isAdmin: boolean;
}

const NodeCard = ({
	node,
	disciplineData,
	siteSettings,
	enterFrom,
	isAdmin,
	onClick,
	onPointerDown,
	onPointerMove,
	onPointerUp,
}: NodeCardProps) => {
	const textRef = useRef<SVGTextElement>(null);
	const cardRef = useRef<SVGGElement>(null);

	const disciplineImg =
		disciplineData?.find((disc) => disc.name === node.data.discipline)?.image ?? undefined;
	const hasImage = !!disciplineImg;
	const ancestors = node.ancestors();
	const ancestorColor: string | undefined =
		node.depth > 1 ? ancestors[ancestors.length - 2]?.data?.color : undefined;

	// Overlay path fill (colour tint over the image)
	const overlayFill = (() => {
		if (node.depth > 1 && !hasImage) return ancestorColor;
		if (node.depth === 1 && !hasImage) return node.data.color;
		if (node.depth <= 1) return 'rgba(0, 0, 0, 0.1)';
		return 'rgba(124, 135, 142, 0.88)';
	})();

	// Text-panel background fill
	const textBgFill = node.depth !== 0 && !hasImage ? 'lightgray' : 'rgba(0, 0, 0, 0.17)';

	// Main rect fill
	const mainRectFill =
		node.depth === 0 ? 'white' : node.depth === 1 ? node.data.color : 'rgba(197, 45, 45, 0.07)';

	// Overlay path shape (full-card for root/discipline, right-side for course)
	const overlayPathD =
		node.depth <= 1
			? `M0 ${CURVE}q0-${CURVE} ${CURVE}-${CURVE}h${WIDTH_EXT_WITH_CURVE}q${CURVE} 0 ${CURVE} ${CURVE}v98q0 ${CURVE}-${CURVE} ${CURVE}h-${WIDTH_EXT_WITH_CURVE}q-${CURVE} 0-${CURVE}-${CURVE}z`
			: `M50, 0 h${WIDTH_WITH_CURVE} q${CURVE},0 ${CURVE}, ${CURVE} v98 q0,${CURVE} -${CURVE},${CURVE} h-${WIDTH_WITH_CURVE + 1} z`;

	const overlayClass =
		node.depth === 0 ? 'org-card' : node.depth === 1 ? 'discipline-card' : 'course-card';

	// Division abbreviation e.g. "Lower School" → "LS"
	const divisionLabel = `${node.data.division?.split('')[0]?.toUpperCase() ?? ''}S`;

	// Font size for course name text
	const fontSize = (() => {
		if (node.depth === 0) return '33px';
		if (node.depth === 1) return '24px';
		if ((node.data.name?.split('').length ?? 0) >= 33) return '18px';
		return '21px';
	})();

	// After the <text> mounts, wrap long names into multiple <tspan> lines and
	// vertically centre discipline/root text. Re-runs when name or depth changes.
	useEffect(() => {
		if (textRef.current && node.depth !== 0 && !isSpacerMapNode(node.data.name)) {
			// Reset to raw text so wrapTextElement starts from a clean slate.
			textRef.current.textContent = node.data.name ?? '';
			wrapTextElement(textRef.current, rectNode.width - 15);
			adjustDisciplineTextPosition(
				textRef.current,
				rectNode.height,
				!!node.data.isDiscipline,
				node.depth
			);
		}
	}, [node.data.name, node.depth, node.data.isDiscipline]);

	// Animate entering nodes from their parent's position (FLIP technique).
	useLayoutEffect(() => {
		if (!enterFrom || !cardRef.current) return;
		const el = cardRef.current;
		el.style.transition = 'none';
		el.style.transform = `translate(${enterFrom.x}px, ${enterFrom.y}px)`;
		el.style.opacity = '0';
		// Force reflow so the browser registers the starting position.
		el.getBoundingClientRect();
		el.style.transition = 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease';
		el.style.transform = `translate(${node.y}px, ${node.x - rectNode.height / 2}px)`;
		el.style.opacity = '1';
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: SVG <g> used as a D3 node container — interactive when clickable
		<g
			ref={cardRef}
			className={`course-${node.data.tracker}`}
			role={onClick ? 'button' : undefined}
			tabIndex={onClick ? 0 : undefined}
			style={{
				transform: `translate(${node.y}px, ${node.x - rectNode.height / 2}px)`,
				transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
				pointerEvents: onClick || onPointerDown ? 'auto' : 'none',
				cursor: onPointerDown ? 'grab' : onClick ? 'pointer' : undefined,
				opacity: !isAdmin && isSpacerMapNode(node.data.name) ? 0 : 1,
			}}
			onClick={onClick}
			onKeyDown={
				onClick
					? (e) => {
							if (e.key === 'Enter' || e.key === ' ') onClick();
						}
					: undefined
			}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
		>
			{isSpacerMapNode(node.data.name) ? (
				<>
					{/* ── Layout-only spacer: visible anchor (admin: removable; parent map: non-interactive) ── */}
					<rect
						x={-11}
						y={-11}
						rx={6}
						className="course-backdrop"
						fill="none"
						width={rectNode.extWidth + 20}
						height={rectNode.height + 20}
					/>
					<rect
						width={rectNode.extWidth}
						height={rectNode.height}
						rx={6}
						fill="rgba(148, 163, 184, 0.22)"
						stroke="rgb(100, 116, 139)"
						strokeWidth={2}
						opacity={isAdmin ? 1 : 0}
						strokeDasharray="7 5"
					/>
					<text
						x={rectNode.extWidth / 2}
						y={rectNode.height / 2}
						textAnchor="middle"
						dominantBaseline="middle"
						fill="rgb(51, 65, 85)"
						className="select-none"
						style={{ fontSize: '15px', fontFamily: 'var(--font-regular)' }}
						opacity={isAdmin ? 1 : 0}
					>
						Spacer
					</text>
				</>
			) : (
				<>
					{/* ── Backdrop (invisible hit area for hover/focus styles) ── */}
					<rect
						x={-11}
						y={-11}
						rx={6}
						className="course-backdrop"
						fill="none"
						width={rectNode.extWidth + 20}
						height={rectNode.height + 20}
					/>

					{/* ── Main card rectangle ── */}
					<rect width={rectNode.extWidth} height={rectNode.height} fill={mainRectFill} rx={6} />

					{/* ── Discipline / org image ── */}
					<image
						height={node.depth === 0 ? IMAGE_HEIGHT_ROOT : rectNode.height}
						y={node.depth === 0 ? (rectNode.height - IMAGE_HEIGHT_ROOT) / 2 : 0}
						x={node.depth === 0 ? (rectNode.width - 200) / 2 + 25 : 0}
						width={node.depth === 0 ? rectNode.extWidth - 50 : rectNode.extWidth}
						preserveAspectRatio={node.depth === 0 ? 'xMidYMid' : 'xMinYMin slice'}
						clipPath="url(#react-round-corner)"
						xlinkHref={node.depth === 0 ? (siteSettings?.logo ?? undefined) : disciplineImg}
					/>

					{/* ── Colour overlay / tint ── */}
					<path d={overlayPathD} fill={overlayFill} className={overlayClass} />

					{/* ── Depth-1: left colour-accent bar ── */}
					{node.depth === 1 && (
						<path
							d={`M-1 ${CURVE - 1}q0-${CURVE - 1} ${CURVE - 1}-${CURVE - 1}h${COLOR_BAR_WIDTH - CURVE} v${rectNode.height + 0.2} h-${COLOR_BAR_WIDTH - CURVE}q-${CURVE - 1} 0-${CURVE - 1}-${CURVE - 1}z`}
							fill={node.data.color}
						/>
					)}

					{/* ── Depth > 1: left text-panel background ── */}
					{node.depth > 1 && (
						<path
							d={`M0 ${CURVE}q0-${CURVE} ${CURVE}-${CURVE}h${TEXT_BG_WIDTH} v${rectNode.height} h-${TEXT_BG_WIDTH}q-${CURVE} 0-${CURVE}-${CURVE}z`}
							fill={textBgFill}
						/>
					)}

					{/* ── Depth > 1: discipline colour dot ── */}
					{node.depth > 1 && (
						<circle
							cx={CENTER_POINT / 2}
							cy={rectNode.height - DOT_RADIUS / 2 - DIVISION_TEXT_SIZE + 5}
							fill={ancestorColor}
							r={DOT_RADIUS}
						/>
					)}

					{/* ── Depth > 1: division abbreviation inside dot ── */}
					{node.depth > 1 && (
						<text
							y={CENTER_POINT / 2}
							x={CENTER_POINT / 2}
							textAnchor="middle"
							dy=".35em"
							fill="white"
							className="select-none"
							style={{
								fontSize: `${DIVISION_TEXT_SIZE}px`,
								dominantBaseline: 'middle',
								textShadow: '-2px 2px 6px rgba(0,0,0,.7)',
							}}
						>
							{divisionLabel}
						</text>
					)}

					{/* ── Course / discipline name — wrapped by useEffect ── */}
					{node.depth !== 0 && (
						<text
							ref={textRef}
							y={CENTER_POINT / 2}
							x={node.depth > 1 ? CENTER_POINT + 10 : CENTER_POINT - 10}
							dy=".35em"
							fill="white"
							className="select-none"
							style={{
								fontSize,
								fontFamily: node.depth > 1 ? 'var(--font-regular)' : 'var(--font-semibold)',
								textShadow: '-2px 2px 6px rgba(0,0,0,.7)',
							}}
						>
							{node.data.name}
						</text>
					)}
				</>
			)}
		</g>
	);
};

// ── Migration 6: TreeLinks ────────────────────────────────────────────────────
// Replaces the D3-imperative link enter/update/exit pattern for the curved
// Bézier connectors between parent and child nodes.
//
// ANIMATION TRADE-OFF: D3 animated link paths from `source` position to final
// position (conditionalTransition). In React the paths render at final positions
// directly. For admin mode (transitions disabled) this is invisible. Non-admin
// enter/exit path animations are lost; zoom/pan and collapse are unaffected.
//
// AUTO-ZOOM: the original `on('start')` callback on the last link transition
// triggered the initial fit-to-screen for non-admin. That logic has been moved
// to a standalone `useEffect` that watches `courseCoordsArray.length`.

const TreeLinks = ({ links }: { links: NodeEntity[] }) =>
	links.map((d) => {
		const ancestors = d.ancestors();
		return (
			<path
				key={`${d.data?.id}-${d.height}`}
				className="link"
				stroke={ancestors[ancestors.length - 2]?.data?.color}
				opacity={1}
				d={d.parent ? (diagonal(d, d.parent) ?? undefined) : undefined}
			/>
		);
	});

// ── Migration 7: MultiParentLinks ─────────────────────────────────────────────
// Replaces the D3-imperative multi-parent-link enter/update/exit pattern for
// supplemental-parent connectors (nodes with a `suppParent` field).

interface MultiParentLink {
	id: string;
	parent: { x: number; y: number };
	x: number;
	y: number;
	discipline: string | undefined;
	name: string;
	color: string | undefined;
}

const MultiParentLinks = ({ links }: { links: MultiParentLink[] }) =>
	links.map((d) => (
		<path
			key={`${d.id}-${d.x}-${d.y}`}
			className="multi-parent-link link"
			stroke={d.color}
			opacity={1}
			d={createCourseMapPaths(d) ?? undefined}
		/>
	));
// ─────────────────────────────────────────────────────────────────────────────

// ── DragGhost ─────────────────────────────────────────────────────────────────
// Translucent card stack that follows the cursor while a node is being dragged.
// Rendered inside the zoomed <g> so coordinates are already in tree-space.
// Up to GHOST_MAX_STACK descendant cards are shown stacked behind the main card.
const GHOST_STACK_OFFSET = 10;
const GHOST_MAX_STACK = 3;

interface DragGhostProps {
	x: number; // tree-space horizontal (= node.y convention)
	y: number; // tree-space vertical   (= node.x convention)
	node: NodeEntity;
	dropTargetName?: string | null;
}

const DragGhost = ({ x, y, node, dropTargetName }: DragGhostProps) => {
	const ancestors = node.ancestors();
	const cardColor =
		node.depth === 1
			? (node.data.color ?? 'var(--org-color)')
			: (ancestors.slice(-2, -1)[0]?.data?.color ?? 'var(--org-color)');

	const allDescendants = node.descendants().slice(1);
	const extraCount = Math.max(0, allDescendants.length - GHOST_MAX_STACK);
	const stackItems = allDescendants.slice(0, GHOST_MAX_STACK);
	const stackShift = stackItems.length * GHOST_STACK_OFFSET;

	return (
		<g
			transform={`translate(${x - rectNode.width / 2},${y - rectNode.height / 2 - stackShift})`}
			style={{ pointerEvents: 'none' }}
		>
			{/* Stacked child cards — painted first so they sit furthest behind */}
			{stackItems.map((child, i) => {
				const idx = stackItems.length - 1 - i;
				const ox = (idx + 1) * GHOST_STACK_OFFSET;
				const oy = (idx + 1) * GHOST_STACK_OFFSET;
				return (
					<g key={child.data?.tracker ?? i} transform={`translate(${ox},${oy})`}>
						<rect
							width={rectNode.width}
							height={rectNode.height}
							rx={6}
							fill={cardColor}
							opacity={0.25 + idx * 0.1}
						/>
						<text
							x={14}
							y={rectNode.height / 2}
							dominantBaseline="middle"
							fill="white"
							fontSize="14px"
							opacity={0.6 + idx * 0.1}
							fontFamily="var(--font-regular)"
							style={{ userSelect: 'none' }}
						>
							{child.data?.name}
						</text>
					</g>
				);
			})}

			{/* Main (front) card */}
			<rect
				x={-4}
				y={-4}
				width={rectNode.width + 8}
				height={rectNode.height + 8}
				rx={10}
				fill="none"
				stroke="rgba(0,0,0,0.25)"
				strokeWidth={8}
				opacity={0.6}
			/>
			<rect width={rectNode.width} height={rectNode.height} rx={6} fill={cardColor} opacity={0.9} />
			<text
				x={14}
				y={rectNode.height / 2}
				dominantBaseline="middle"
				fill="white"
				fontSize="17px"
				fontFamily="var(--font-semibold)"
				style={{ textShadow: '-1px 2px 5px rgba(0,0,0,.6)', userSelect: 'none' }}
			>
				{node.data.name}
			</text>

			{/* Overflow badge */}
			{extraCount > 0 && (
				<g transform={`translate(${rectNode.width - 30},${-12})`}>
					<circle r={15} fill={cardColor} stroke="white" strokeWidth={2.5} />
					<text
						textAnchor="middle"
						dominantBaseline="middle"
						fill="white"
						fontSize="11px"
						fontFamily="var(--font-semibold)"
						style={{ userSelect: 'none' }}
					>
						+{extraCount}
					</text>
				</g>
			)}

			{/* Drop-target label — shows where the card will land */}
			{dropTargetName && (
				<g transform={`translate(${rectNode.width / 2},${rectNode.height + stackShift + 14})`}>
					<rect x={-64} y={0} width={128} height={24} rx={12} fill="rgba(0,0,0,0.55)" />
					<text
						x={0}
						y={12}
						textAnchor="middle"
						dominantBaseline="middle"
						fill="white"
						fontSize="12px"
						fontFamily="var(--font-semibold)"
						style={{ userSelect: 'none' }}
					>
						→ {dropTargetName}
					</text>
				</g>
			)}
		</g>
	);
};

// ── DropHighlight ──────────────────────────────────────────────────────────────
// Pulsing border drawn around the active drop target.
// Rendered LAST in SVG paint order so it always sits on top of all cards.
// The ghost's "→ name" label handles communicating where the card will land,
// so no badge is needed here.
interface DropHighlightProps {
	node: NodeEntity;
}

const DropHighlight = ({ node }: DropHighlightProps) => (
	<g
		transform={`translate(${node.y},${node.x - rectNode.height / 2})`}
		style={{ pointerEvents: 'none' }}
	>
		{/* Glow shadow behind the marching border — makes it visible on dark & light cards */}
		<rect
			x={-8}
			y={-8}
			width={rectNode.extWidth + 16}
			height={rectNode.height + 16}
			rx={12}
			fill="none"
			stroke="rgba(255,255,255,0.35)"
			strokeWidth={10}
		/>
		{/* Marching-ants dashed border */}
		<rect
			x={-8}
			y={-8}
			width={rectNode.extWidth + 16}
			height={rectNode.height + 16}
			rx={12}
			fill="rgba(255,255,255,0.07)"
			stroke="red"
			strokeWidth={3}
			strokeDasharray="18 10"
			style={{ animation: 'drop-target-march 0.5s linear infinite' }}
		/>
	</g>
);
// ─────────────────────────────────────────────────────────────────────────────

declare module 'd3-selection' {
	interface Selection<GElement extends d3.BaseType, Datum, PElement extends d3.BaseType, PDatum> {
		conditionalTransition(
			isAdmin: boolean,
			duration: number
		): Selection<GElement, Datum, PElement, PDatum>;
	}
}

d3.selection.prototype.conditionalTransition = function (isAdmin: boolean, duration: number) {
	return !isAdmin ? this.transition().duration(duration) : this;
};

interface CourseMapProps {
	isAdmin?: boolean;
	passedEvent?: (a: string) => void;
}

function formatRelativeTime(date: Date): string {
	const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
	if (seconds < 60) return 'just now';
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	return `${Math.floor(hours / 24)}d ago`;
}

const CourseMapD3React = ({ isAdmin = true, passedEvent }: CourseMapProps) => {
	const svgChartRef = useRef<HTMLDivElement>(null);
	const [treeData, setTreeData] = useState<CourseMapRoot>({
		name: `${import.meta.env.VITE_ORG}`,
		id: 'root',
		tracker: 'root',
		children: [],
	});

	const {
		setCourseCoordsArray,
		setCourseSearchNodes,
		clearCourseMapTransientState,
		courseCoordsArray,
		duration,
		zoom,
		d3InstanceRef,
		pushHistory,
		undo: undoHistory,
		redo: redoHistory,
		clearHistory,
		past,
		future,
	} = useCourseMapStore();
	const { disciplines: disciplineData, siteSettings } = useContext(GlobalContext);

	useEffect(() => {
		return () => {
			clearCourseMapTransientState();
		};
	}, [clearCourseMapTransientState]);

	const treeRef = useRef({});
	const svgRef = useRef<SVGSVGElement | null>(null);
	// Holds a JSON snapshot of treeData from the last completed render.
	// Because callers mutate the D3 hierarchy in-place BEFORE calling
	// updateTreeData, `treeData` itself already reflects the new state by
	// the time pushHistory runs. Using this ref — updated only after each
	// render — guarantees we snapshot the pre-mutation state for undo.
	const treeSnapshotRef = useRef<string>('');
	const [isMenuActive, setIsMenuActive] = useState(false);
	const [isMapAltered, setIsMapAltered] = useState(false);
	// 'view'   — pan/zoom only, no icons shown
	// 'edit'   — + circles visible, drag-to-rearrange enabled
	// 'remove' — − circles visible, no drag
	const [mapMode, setMapMode] = useState<'view' | 'edit' | 'remove'>('view');
	const isAddingCourse = mapMode === 'edit';
	const [isConfirming, setIsConfirming] = useState(false);
	const [currentCourse, setCurrentCourse] = useState<CourseWithRelations | null>(null);
	const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);

	// ── TUTORIAL STATE ────────────────────────────────────────────────────────
	const [isTutorialEnabled, setIsTutorialEnabled] = useState(() => {
		try {
			return JSON.parse(localStorage.getItem('elevateAdminCourseMapTutorial') ?? 'true') as boolean;
		} catch {
			return true;
		}
	});

	// ── MIGRATION STATE ──────────────────────────────────────────────────────
	// Tracks the current D3 zoom transform so the React overlay stays aligned.
	const [zoomTransform, setZoomTransform] = useState<d3.ZoomTransform | null>(null);
	// Tracks which node's add-button is "active" (pulse animation) so we no
	// longer need d3.select(...).classed('active-icon', true/false).
	const [activeTrackerId, setActiveTrackerId] = useState<string | null>(null);
	// Link data computed by update() and stored here so TreeLinks /
	// MultiParentLinks can render them in the React overlay.
	const [treeLinks, setTreeLinks] = useState<NodeEntity[]>([]);
	const [multiParentLinks, setMultiParentLinks] = useState<MultiParentLink[]>([]);

	// ── DRAG STATE ───────────────────────────────────────────────────────────
	// Cursor position in tree-coordinate space while a drag is active.
	// Tree coords: x = horizontal (maps to node.y), y = vertical (maps to node.x)
	interface DragStateType {
		node: NodeEntity;
		x: number;
		y: number;
	}
	const [dragState, setDragState] = useState<DragStateType | null>(null);
	// Discipline node the cursor is hovering — highlighted as the drop target.
	const [dropTarget, setDropTarget] = useState<NodeEntity | null>(null);
	// Refs for pointer/D3 handlers — avoids stale closures without re-creating
	// callbacks on every render.
	const isDraggingRef = useRef(false);
	const dragNodeRef = useRef<NodeEntity | null>(null);
	const dropTargetRef = useRef<NodeEntity | null>(null);
	const pointerDownRef = useRef<{ clientX: number; clientY: number } | null>(null);
	const dragThresholdPassed = useRef(false);
	// Mirror of courseCoordsArray kept current via a useEffect so drag handlers
	// always read fresh node positions without needing deps in useCallback.
	const courseCoordsRef = useRef<NodeEntity[]>([]);
	// rAF handle — throttles pointermove to one React re-render per frame.
	const rafRef = useRef<number | null>(null);
	// Trackers of all descendants of the dragged node — computed once when drag
	// starts so JSX can apply dimming with an O(1) Set lookup per node.
	const descendantTrackersRef = useRef<Set<string>>(new Set());
	// Auto-pan: separate RAF loop that pans the map when the pointer is near
	// an edge during a drag, and the latest pointer client position during drag.
	const autoPanRafRef = useRef<number | null>(null);
	const dragClientPosRef = useRef<{ x: number; y: number } | null>(null);
	// Ref to the D3 <g> group so auto-pan can update it without going through
	// the zoomed() handler (which is blocked while isDraggingRef is true).
	const gRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
	// D3's `update` function (defined inside useLayoutEffect) stored here so
	// the React overlay can trigger collapse/expand for non-admin discipline clicks.
	const d3UpdateRef = useRef<((source: NodeEntity) => void) | null>(null);
	// ── Enter animation state ───────────────────────────────────────────────
	// When a discipline is clicked, we record the source card position so
	// entering child nodes can animate FROM the parent instead of appearing.
	const enterSourceRef = useRef<{ x: number; y: number } | null>(null);
	const prevNodeTrackersRef = useRef<Set<string>>(new Set());
	// Set to true when a discipline click triggers expand/collapse so the
	// fit-to-view effect knows to adjust the zoom on the next render.
	const shouldFitViewRef = useRef(false);
	// ─────────────────────────────────────────────────────────────────────────

	const queryClient = useQueryClient();

	const { mutate: mapMutation } = useMutation({
		mutationFn: updateCourseMap,
		onSuccess: () => {
			showSuccessToast('Course Map Updated');
			setIsMapAltered(false);
			clearHistory();
			queryClient.invalidateQueries({ queryKey: ['get-course-alignment'] });
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	// Called from the history drawer's "Confirm & Save" button.
	const commitSave = () => {
		setIsHistoryDrawerOpen(false);
		mapMutation({ map: treeData });
	};

	// Opens the review drawer; the user confirms before the map is persisted.
	const saveMapChanges = () => setIsHistoryDrawerOpen(true);

	const { isLoading, isFetched, data } = useQuery({
		queryKey: ['get-course-map', import.meta.env.VITE_SCHOOL_ID],
		queryFn: getCourseMap,
		staleTime: Infinity,
		refetchOnMount: 'always',
	});

	useEffect(() => {
		if (data) {
			treeRef.current = JSON.stringify(data);
			if (isEmpty(data)) {
				setTreeData({
					name: `${import.meta.env.VITE_ORG}`,
					id: `${import.meta.env.VITE_ORG?.replace(/\s/g, '')}-0`,
					tracker: 'root',
					children: [],
				});
				return;
			}
			setTreeData(data);
		}
	}, [data]);

	const { mutate: courseDeleteMutation } = useMutation({
		mutationFn: deleteCourse,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['get-admin-panel-courses'] });
			showSuccessToast('Course Deleted');
			setIsMapAltered(false);
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const rootNode = useCallback((node: NodeEntity): NodeEntity | null => {
		if (node) {
			const ancestors = node.ancestors();
			return ancestors[ancestors.length - 1];
		}
		return null;
	}, []);

	const updateTreeData = useCallback(
		(treeValue: CourseNodeBase, label = 'Map updated') => {
			// treeSnapshotRef.current holds the JSON from the LAST render — before
			// any in-place mutations made by the caller — so this is always the
			// correct "before" state for the undo stack.
			pushHistory(treeSnapshotRef.current, label);
			const currentTree = JSON.stringify(treeValue);
			// Advance the ref immediately so rapid back-to-back calls each get a
			// fresh "before" snapshot instead of the same stale one.
			treeSnapshotRef.current = currentTree;
			setTreeData({ ...treeValue } as CourseMapRoot);
			setIsMapAltered(treeRef.current !== currentTree);
		},
		[pushHistory]
	);

	const findMatchingNode = useCallback(
		(nodeRef: NodeEntity | NodeEntity[], id: string): NodeEntity | null => {
			if (Array.isArray(nodeRef)) {
				for (const child of nodeRef) {
					const found = findMatchingNode(child, id);
					if (found) return found;
				}
				return null;
			}
			if (nodeRef.data?.tracker === id) return nodeRef;
			if (nodeRef.children) return findMatchingNode(nodeRef.children, id);
			return null;
		},
		[]
	);

	const removeCourse = useCallback(() => {
		const id = currentCourse?.course_id;
		if (!id) return;
		courseDeleteMutation({ id });
		const nodeTree = d3.hierarchy<CourseNodeBase>(
			treeData,
			(d) => d.children
		) as unknown as NodeEntity;
		const sn = findMatchingNode(nodeTree, id);

		if (!sn) return;

		const root = rootNode(sn);

		if (sn.children !== undefined) {
			const idx = sn.parent!.data.children.findIndex(
				(child: CourseNodeBase) => child.tracker === sn.data.tracker
			);
			const newChildList = (sn.parent!.children ?? []).filter(
				(child: NodeEntity) => child.data.tracker !== sn.data.tracker
			);
			const newDataList = sn.parent!.data.children.filter(
				(child: CourseNodeBase) => child.tracker !== sn.data.tracker
			);
			newChildList.splice(idx, 0, ...(sn.children ?? []));
			newDataList.splice(idx, 0, ...sn.data.children);
			sn.parent!.children = [...newChildList];
			sn.parent!.data.children = [...newDataList];
			updateTreeData({ ...root?.data } as CourseNodeBase, `Removed '${sn.data.name}'`);
		} else {
			sn.parent!.data.children = sn.parent!.data.children.filter(
				(child: CourseNodeBase) => child.tracker !== sn.data.tracker
			);
			updateTreeData({ ...root?.data } as CourseNodeBase, `Removed '${sn.data.name}'`);
		}
	}, [currentCourse, courseDeleteMutation, findMatchingNode, treeData, rootNode, updateTreeData]);

	const confirmDelete = useCallback((course: CourseWithRelations) => {
		setCurrentCourse(course);
		setIsConfirming(true);
	}, []);

	const checkTreeDiff = useCallback(
		(a: FormSubmit) => {
			function eachRecursive(obj: Record<string, unknown>) {
				for (const k in obj) {
					if (Object.hasOwn(obj, k)) {
						if (typeof obj[k] === 'object' && obj[k] !== null)
							eachRecursive(obj[k] as Record<string, unknown>);
						if (obj[k] === a.courseId) {
							obj.discipline = a.disciplineEdits;
							obj.division = a.divisionEdits;
							obj.name = a.courseNameEdits;
							obj.grade = a.grade;
							obj.isAp = a.isAp;
							obj.isArchived = a.isArchived;
						}
					}
				}
			}
			eachRecursive(treeData as unknown as Record<string, unknown>);
			const label = `Updated '${a.courseNameEdits}'`;
			updateTreeData({ ...treeData } as CourseMapRoot, label);
			setIsHistoryDrawerOpen(true);
		},
		[treeData, updateTreeData]
	);

	const [selectedNode, setSelectedNode] = useState<NodeEntity>();
	// When set, renders the AddCoursePopover anchored to this node's + button.
	const [addPopoverNode, setAddPopoverNode] = useState<NodeEntity | null>(null);
	// When set, renders the MergePopover anchored to this node's merge button.
	const [mergePopoverNode, setMergePopoverNode] = useState<NodeEntity | null>(null);
	const [newCourse, setNewCourse] = useState({
		data: { name: '' },
		depth: 0,
		height: 0,
		id: '',
		parent: {},
	});

	const zoomRef = useRef<{ k: number | null; x: number | null; y: number | null }>({
		k: null,
		x: null,
		y: null,
	});

	const openPanel = () => {
		setIsMenuActive(!isMenuActive);
		if (isMenuActive) setActiveTrackerId(null);
	};

	// Replaces the `on('start')` callback that was attached to link transitions.
	// Triggers the initial fit-to-screen zoom for non-admin when the tree first
	// loads with exactly 10 nodes (root + 9 courses).
	useEffect(() => {
		if (isAdmin || !d3InstanceRef.current || !svgChartRef.current || !zoom.current) return;
		const nodeCount = courseCoordsArray.length;
		const clientWidth = (svgChartRef.current.clientWidth || 600) - 300;
		const clientHeight = svgChartRef.current.clientHeight || 600;
		if (nodeCount === 10 && d3InstanceRef.current) {
			zoom.current!.transform(
				d3InstanceRef.current
					.interrupt()
					.transition()
					.duration(duration) as unknown as d3.Selection<SVGSVGElement, unknown, null, undefined>,
				d3.zoomIdentity.translate(clientWidth / 4.5, clientHeight / 2).scale(0.6)
			);
			setTimeout(() => {
				document.querySelector('.initial-node')?.classList.add('stop-zoom');
			}, duration);
		} else {
			document.querySelector('.initial-node')?.classList.remove('stop-zoom');
		}
	}, [courseCoordsArray, isAdmin, duration, zoom, d3InstanceRef]);

	// Keep courseCoordsRef current so drag handlers always read fresh positions.
	useEffect(() => {
		courseCoordsRef.current = courseCoordsArray ?? [];
	}, [courseCoordsArray]);

	// After a discipline expand/collapse, smoothly zoom to fit all visible nodes.
	useEffect(() => {
		if (!shouldFitViewRef.current) return;
		shouldFitViewRef.current = false;

		const nodes = courseCoordsArray as NodeEntity[] | undefined;
		if (!nodes?.length || !d3InstanceRef.current || !svgChartRef.current || !zoom.current) return;

		const vw = svgChartRef.current.clientWidth || 600;
		const vh = svgChartRef.current.clientHeight || 600;

		let minX = Infinity;
		let maxX = -Infinity;
		let minY = Infinity;
		let maxY = -Infinity;
		for (const n of nodes) {
			if (n.y < minX) minX = n.y;
			if (n.y + rectNode.extWidth > maxX) maxX = n.y + rectNode.extWidth;
			if (n.x - rectNode.height / 2 < minY) minY = n.x - rectNode.height / 2;
			if (n.x + rectNode.height / 2 > maxY) maxY = n.x + rectNode.height / 2;
		}

		const padding = 60;
		const treeW = maxX - minX || 1;
		const treeH = maxY - minY || 1;
		const scale = Math.max(
			0.15,
			Math.min((vw - padding * 2) / treeW, (vh - padding * 2) / treeH, 0.8)
		);

		const tx = vw / 2 - ((minX + maxX) / 2) * scale;
		const ty = vh / 2 - ((minY + maxY) / 2) * scale;

		zoom.current.transform(
			d3InstanceRef.current.interrupt().transition().duration(duration) as unknown as d3.Selection<
				SVGSVGElement,
				unknown,
				null,
				undefined
			>,
			d3.zoomIdentity.translate(tx, ty).scale(scale)
		);
	}, [courseCoordsArray, duration, zoom, d3InstanceRef]);

	// Update the set of rendered trackers AFTER each render so the overlay can
	// detect entering nodes on the NEXT change. Also clear the enter source.
	useEffect(() => {
		prevNodeTrackersRef.current = new Set(
			(courseCoordsArray as NodeEntity[] | undefined)?.map((n) => n.data.tracker) ?? []
		);
		enterSourceRef.current = null;
	}, [courseCoordsArray]);

	// Keep treeSnapshotRef current with the pre-mutation state. This is updated
	// AFTER every render (so it always reflects the last committed treeData),
	// giving updateTreeData a correct "before" snapshot even when callers have
	// already mutated the D3 hierarchy in-place before calling it.
	useEffect(() => {
		treeSnapshotRef.current = JSON.stringify(treeData);
	}, [treeData]);

	// ── React handlers for migrated nodes ────────────────────────────────────
	const handleNodeAddClick = useCallback(
		(sn: NodeEntity) => {
			setMergePopoverNode(null);
			setSelectedNode({ ...sn });
			const newNode = { name: '', children: [] as CourseNodeBase[], new: false };
			const hierarchy = d3.hierarchy<typeof newNode>(newNode);
			const createdHierarchy = {
				...hierarchy,
				depth: sn.depth + 1,
				height: sn.height - 1,
				parent: sn,
				id: '',
			};
			createdHierarchy.data.new = true;
			setNewCourse({ ...createdHierarchy });
			setAddPopoverNode(sn);
			setActiveTrackerId(sn.data.tracker);
		},
		[setSelectedNode, setNewCourse]
	);

	const handleNodeRemoveClick = useCallback(
		(sn: NodeEntity) => {
			if (sn.depth === 0) return;
			const idMatch = sn.id;
			const root = rootNode(sn);
			if (sn.children !== undefined) {
				const idx = sn.parent!.data.children.findIndex((child) => child.id === idMatch);
				const newChildList = (sn.parent!.children ?? []).filter((child) => child.id !== sn.id);
				const newDataList = sn.parent!.data.children.filter((child) => child.id !== sn.id);
				newChildList.splice(idx, 0, ...(sn.children ?? []));
				newDataList.splice(idx, 0, ...sn.data.children);
				sn.parent!.children = [...newChildList];
				sn.parent!.data.children = [...newDataList];
				updateTreeData({ ...root?.data } as CourseNodeBase, `Removed '${sn.data.name}' from map`);
			} else {
				sn.parent!.data.children = sn.parent!.data.children.filter((child) => child.id !== sn.id);
				updateTreeData({ ...root?.data } as CourseNodeBase, `Removed '${sn.data.name}' from map`);
			}
		},
		[rootNode, updateTreeData]
	);

	const handleMoveUp = useCallback(
		(node: NodeEntity) => {
			const parentChildren = node.parent!.children ?? [];
			const idx = parentChildren.findIndex((x) => x.data.id === node.data.id);
			const idx2 = node.parent!.data.children.findIndex((x) => x.id === node.data.id);
			const tree = { ...node.parent! };
			const lastIdx = (parentChildren.length ?? 1) - 1;
			if (idx === 0) {
				moveChildrenArray(tree.children!, idx, lastIdx);
				moveChildrenArray(tree.data.children, idx2, lastIdx);
			} else {
				moveChildrenArray(tree.children!, idx, idx - 1);
				moveChildrenArray(tree.data.children, idx2, idx2 - 1);
			}
			updateTreeData(tree.data, `Reordered '${node.data.name}'`);
		},
		[updateTreeData]
	);

	const handleMoveDown = useCallback(
		(node: NodeEntity) => {
			const parentChildren = node.parent!.children ?? [];
			const idx = parentChildren.findIndex((x) => x.data.id === node.data.id);
			const idx2 = node.parent!.data.children.findIndex((x) => x.id === node.data.id);
			const tree = { ...node.parent! };
			const lastIdx = (parentChildren.length ?? 1) - 1;
			if (idx === lastIdx) {
				moveChildrenArray(tree.children!, idx, 0);
				moveChildrenArray(tree.data.children, idx2, 0);
			} else {
				moveChildrenArray(tree.children!, idx, idx + 1);
				moveChildrenArray(tree.data.children, idx2, idx2 + 1);
			}
			updateTreeData(tree.data, `Reordered '${node.data.name}'`);
		},
		[updateTreeData]
	);

	const handleMergeClick = useCallback(
		(sn: NodeEntity) => {
			setSelectedNode(sn);
			setIsMenuActive(false);
			setMergePopoverNode(sn);
		},
		[setSelectedNode, setIsMenuActive]
	);

	// ── Drag-and-drop handlers ────────────────────────────────────────────────
	// Uses Pointer Events + setPointerCapture so the drag continues even if the
	// cursor leaves the card element mid-gesture.
	//
	// Coordinate system: D3's tree lays out nodes with x=vertical, y=horizontal.
	// The zoomTransform lets us convert clientX/Y → tree coordinates.
	//
	// Performance strategy:
	//   • handlePointerDown  — records starting client position only.
	//   • handlePointerMove  — throttled with requestAnimationFrame so React only
	//                          re-renders once per browser animation frame.
	//   • handlePointerUp    — commits the move by mutating the data tree.
	const DRAG_THRESHOLD = 8; // px before we commit to a drag gesture

	// ── EDGE AUTO-PAN ────────────────────────────────────────────────────────
	// When the pointer is near the SVG edge during a drag we translate the zoom
	// transform each frame so the user can reach off-screen drop targets.
	// We bypass the `zoomed` D3 handler (which is blocked while isDraggingRef
	// is true) and apply the transform directly to both the D3 <g> group and
	// the React zoomTransform state so the overlay SVG stays aligned.
	const stopAutoPan = useCallback(() => {
		if (autoPanRafRef.current !== null) {
			cancelAnimationFrame(autoPanRafRef.current);
			autoPanRafRef.current = null;
		}
		dragClientPosRef.current = null;
	}, []);

	const startAutoPan = useCallback(() => {
		const EDGE_ZONE = 100; // px from SVG edge that activates panning
		const MAX_SPEED = 14; // max px/frame to translate at the very edge

		const tick = () => {
			const pos = dragClientPosRef.current;
			const svgEl = svgRef.current;
			if (!pos || !svgEl || !gRef.current || !isDraggingRef.current) {
				autoPanRafRef.current = null;
				return;
			}

			const rect = svgEl.getBoundingClientRect();

			// How deep into the edge zone (0 = at the boundary, 1 = at the pixel edge)
			const leftRatio = Math.max(0, (EDGE_ZONE - (pos.x - rect.left)) / EDGE_ZONE);
			const rightRatio = Math.max(0, (EDGE_ZONE - (rect.right - pos.x)) / EDGE_ZONE);
			const topRatio = Math.max(0, (EDGE_ZONE - (pos.y - rect.top)) / EDGE_ZONE);
			const bottomRatio = Math.max(0, (EDGE_ZONE - (rect.bottom - pos.y)) / EDGE_ZONE);

			const dx = (leftRatio - rightRatio) * MAX_SPEED;
			const dy = (topRatio - bottomRatio) * MAX_SPEED;

			if (dx !== 0 || dy !== 0) {
				const cur = zoomRef.current;
				if (cur.x == null || cur.y == null || cur.k == null) return;
				const next = d3.zoomIdentity.translate(cur.x + dx, cur.y + dy).scale(cur.k);
				zoomRef.current = next;
				gRef.current?.attr('transform', next.toString());
				setZoomTransform(next);
			}

			autoPanRafRef.current = requestAnimationFrame(tick);
		};

		autoPanRafRef.current = requestAnimationFrame(tick);
	}, []);
	// ─────────────────────────────────────────────────────────────────────────

	const handlePointerDown = useCallback(
		(node: NodeEntity) => (e: React.PointerEvent<SVGGElement>) => {
			if (node.depth < 1) return;
			pointerDownRef.current = { clientX: e.clientX, clientY: e.clientY };
			dragNodeRef.current = node;
			dragThresholdPassed.current = false;
		},
		[]
	);

	const handlePointerMove = useCallback(
		(node: NodeEntity) => (e: React.PointerEvent<SVGGElement>) => {
			if (!pointerDownRef.current || dragNodeRef.current?.data.tracker !== node.data.tracker)
				return;

			const dx = e.clientX - pointerDownRef.current.clientX;
			const dy = e.clientY - pointerDownRef.current.clientY;

			if (!dragThresholdPassed.current) {
				if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
				// Threshold crossed — capture pointer so drag continues off-element
				e.currentTarget.setPointerCapture(e.pointerId);
				dragThresholdPassed.current = true;
				isDraggingRef.current = true;
				// Close any open popovers when a drag gesture starts
				setAddPopoverNode(null);
				setMergePopoverNode(null);
				// Pre-compute descendant trackers once so JSX can dim them cheaply.
				const trackers = new Set<string>();
				dragNodeRef.current
					?.descendants()
					.slice(1)
					.forEach((n) => {
						if (n.data?.tracker) trackers.add(n.data.tracker);
					});
				descendantTrackersRef.current = trackers;
				// Start the edge auto-pan loop now that we're in a committed drag.
				dragClientPosRef.current = { x: e.clientX, y: e.clientY };
				startAutoPan();
			}

			// Keep the pointer position up-to-date so the auto-pan loop uses fresh coords.
			dragClientPosRef.current = { x: e.clientX, y: e.clientY };

			// Throttle to one React re-render per animation frame
			if (rafRef.current !== null) return;
			const clientX = e.clientX;
			const clientY = e.clientY;
			rafRef.current = requestAnimationFrame(() => {
				rafRef.current = null;
				if (!dragNodeRef.current || !zoomTransform) return;

				// Convert client coords → tree-coordinate space.
				// Use svgRef directly (more reliable than querySelector).
				const rect = svgRef.current?.getBoundingClientRect();
				if (!rect) return;
				const screenX = clientX - rect.left;
				const screenY = clientY - rect.top;
				const treeX = (screenX - zoomTransform.x) / zoomTransform.k;
				const treeY = (screenY - zoomTransform.y) / zoomTransform.k;

				setDragState({ node: dragNodeRef.current, x: treeX, y: treeY });

				// Find nearest valid drop target — any node except:
				//   • the dragged node itself
				//   • any of its descendants (already stored in descendantTrackersRef)
				//   • its current parent (would be a no-op)
				//   • the root (depth 0, nothing sits above it)
				const draggedNode = dragNodeRef.current;
				if (!draggedNode) return;
				const selfTracker = draggedNode.data?.tracker;
				const currentParentTracker = draggedNode.parent?.data.tracker;

				const candidates = courseCoordsRef.current.filter(
					(n) =>
						n.depth >= 1 &&
						n.data.tracker !== selfTracker &&
						n.data.tracker !== currentParentTracker &&
						!descendantTrackersRef.current.has(n.data.tracker)
				);
				if (candidates.length === 0) {
					setDropTarget(null);
					dropTargetRef.current = null;
					return;
				}

				// Weighted scoring: prefer the node's natural parent depth (depth - 1)
				// so the most intuitive target wins when two candidates are equidistant.
				// Non-natural-depth candidates need to be 40% closer to override.
				const naturalDepth = draggedNode.depth - 1;
				const DEPTH_PENALTY = 1.4;
				let best: NodeEntity | null = null;
				let bestWeighted = Infinity;
				let bestRawDist = Infinity;
				for (const cand of candidates) {
					const dist = Math.hypot(cand.y - treeX, cand.x - treeY);
					const weighted = cand.depth === naturalDepth ? dist : dist * DEPTH_PENALTY;
					if (weighted < bestWeighted) {
						bestWeighted = weighted;
						bestRawDist = dist;
						best = cand;
					}
				}
				// Scale snap radius by 1/k so the visual snap distance stays ~300px
				// regardless of zoom level (at k=0.15 zoomed out, tree radius = 2000).
				const SNAP_RADIUS = 300 / zoomTransform.k;
				// Raw distance determines whether we're close enough; weighted score
				// determined which candidate won.
				const nextTarget = bestRawDist < SNAP_RADIUS ? best : null;
				if (nextTarget?.data.tracker !== dropTargetRef.current?.data.tracker) {
					dropTargetRef.current = nextTarget;
					setDropTarget(nextTarget);
				}
			});
		},
		[zoomTransform, startAutoPan]
	);

	const handlePointerUp = useCallback(
		(_node: NodeEntity) => (e: React.PointerEvent<SVGGElement>) => {
			// Cancel any pending animation frame
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}

			if (dragThresholdPassed.current && dropTargetRef.current && dragNodeRef.current) {
				const dragged = dragNodeRef.current;
				const target = dropTargetRef.current;

				if (dragged.parent) {
					dragged.parent.data.children = dragged.parent.data.children.filter(
						(c) => c.tracker !== dragged.data.tracker
					);
				}
				if (!target.data.children) target.data.children = [];
				target.data.children.push(dragged.data);

				const root = rootNode(dragged);
				updateTreeData(
					{ ...root?.data } as CourseNodeBase,
					`Moved '${dragged.data.name}' → '${target.data.name}'`
				);
			}

			// Release pointer capture if we captured it
			if (dragThresholdPassed.current) {
				try {
					e.currentTarget.releasePointerCapture(e.pointerId);
				} catch {
					// ignore — element may have been removed
				}
			}

			// Stop the edge auto-pan loop before clearing isDraggingRef so the
			// loop's self-cancel guard fires correctly.
			stopAutoPan();

			// Clean up all drag state
			isDraggingRef.current = false;
			dragNodeRef.current = null;
			dropTargetRef.current = null;
			pointerDownRef.current = null;
			dragThresholdPassed.current = false;
			descendantTrackersRef.current = new Set();
			setDragState(null);
			setDropTarget(null);
		},
		[rootNode, updateTreeData, stopAutoPan]
	);
	// Shared cleanup — called by both Escape key and handlePointerUp (no-commit path).
	const cancelDrag = useCallback(() => {
		if (rafRef.current !== null) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
		stopAutoPan();
		isDraggingRef.current = false;
		dragNodeRef.current = null;
		dropTargetRef.current = null;
		pointerDownRef.current = null;
		dragThresholdPassed.current = false;
		descendantTrackersRef.current = new Set();
		setDragState(null);
		setDropTarget(null);
	}, [stopAutoPan]);

	// Cancel the current drag when the user presses Escape.
	useEffect(() => {
		if (!dragState) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') cancelDrag();
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [dragState, cancelDrag]);

	// Named undo/redo handlers — shared between keyboard shortcut and toolbar buttons.
	// treeSnapshotRef.current is the correct "present" snapshot to pass to the store
	// (it hasn't been mutated in-place, unlike treeData which may be stale).
	const handleUndo = useCallback(() => {
		const snapshot = undoHistory(treeSnapshotRef.current);
		if (snapshot) {
			const restored = JSON.parse(snapshot) as CourseMapRoot;
			treeSnapshotRef.current = snapshot;
			setTreeData({ ...restored });
			setIsMapAltered(treeRef.current !== snapshot);
		}
	}, [undoHistory]);

	const handleRedo = useCallback(() => {
		const snapshot = redoHistory(treeSnapshotRef.current);
		if (snapshot) {
			const restored = JSON.parse(snapshot) as CourseMapRoot;
			treeSnapshotRef.current = snapshot;
			setTreeData({ ...restored });
			setIsMapAltered(treeRef.current !== snapshot);
		}
	}, [redoHistory]);

	// Undo  — Cmd/Ctrl+Z
	// Redo  — Cmd/Ctrl+Shift+Z  (also Cmd/Ctrl+Y on Windows)
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			const mod = e.metaKey || e.ctrlKey;
			if (!mod) return;
			if (e.key === 'z' && !e.shiftKey) {
				e.preventDefault();
				handleUndo();
			} else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
				e.preventDefault();
				handleRedo();
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [handleUndo, handleRedo]);
	// ─────────────────────────────────────────────────────────────────────────

	useLayoutEffect(() => {
		function renderMap() {
			const margin: MapMargin = { top: 40, right: 90, bottom: 50, left: 90 };
			const clientWidth = (svgChartRef.current?.clientWidth || 600) - 300;
			const clientHeight = svgChartRef.current?.clientHeight || 600;
			const width = clientWidth + 300 - margin.left - margin.right;
			const height = clientHeight - margin.top - margin.bottom;

			const svgEl = d3.select(svgRef.current!);
			svgEl.selectAll('*').remove();

			const svg = svgEl.attr('width', '100%').attr('height', '100%').attr('class', 'course-map');
			d3InstanceRef.current = svg as d3.Selection<SVGSVGElement | null, unknown, null, undefined>;
			const g = svg.append('g');
			gRef.current = g;

			const nodeSpacing = isAdmin ? 2.5 : 1.8;
			const treeMap = d3
				.tree<CourseNodeBase>()
				.nodeSize([1, clientHeight / nodeSpacing])
				.separation(() => rectNode.height * 1.3);

			const root = d3.hierarchy<CourseNodeBase>(
				treeData,
				(d) => d.children
			) as unknown as NodeEntity;
			root.x0 = height / 2;
			root.y0 = 0;

			const mapNodeLength = treeMap(
				root as unknown as d3.HierarchyNode<CourseNodeBase>
			).descendants().length;

			if (!isAdmin) root.children?.forEach(collapseTree);

			// ── MIGRATION CHANGE ────────────────────────────────────────────
			// `setZoomTransform` is now called here so React knows the current
			// pan/zoom state and can position the overlay SVG accurately.
			const zoomed = (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
				// Don't move the canvas while the user is dragging a card.
				if (isDraggingRef.current) return;
				zoomRef.current = event.transform;
				g.attr('transform', event.transform.toString());
				setZoomTransform(event.transform); // ← share with React
			};
			// ────────────────────────────────────────────────────────────────

			const currentK = zoomRef.current.k ?? 0.5;
			const currentX = zoomRef.current.x || clientWidth / 4.5;

			zoom.current = d3
				.zoom<SVGSVGElement, unknown>()
				.scaleExtent([0.15, 1])
				.translateExtent(createPanLimits(mapNodeLength, margin, clientWidth, isAdmin))
				.extent([
					[0, 0],
					[width, height],
				])
				.on('zoom', zoomed)
				.on('start', (e) => {
					if (e.sourceEvent) d3.select(svgRef.current).classed('dragging', true);
				})
				.on('end', () => d3.select(svgRef.current).classed('dragging', false));

			svg.call(zoom.current!);

			let currentY = 0;
			if (isAdmin) {
				currentY = zoomRef.current.y ?? clientHeight / 2 + 102;
			} else {
				currentY = clientHeight / 2;
			}

			// Applying the initial transform triggers `zoomed`, which calls
			// `setZoomTransform` — so the overlay is aligned from first render.
			svg.call(
				zoom.current!.transform,
				d3.zoomIdentity.translate(currentX, currentY).scale(currentK)
			);

			const defs = svg.append('defs');
			const clipPath = defs.append('clipPath').attr('id', 'round-corner');
			clipPath
				.append('rect')
				.attr('x', 0)
				.attr('y', 0)
				.attr('width', rectNode.extWidth)
				.attr('height', rectNode.height)
				.attr('rx', '6');

			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			d3UpdateRef.current = update;
			update(root);

			function generateRandomId(length = 8) {
				const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
				let randomId = '';
				for (let i = 0; i < length; i += 1) {
					randomId += characters.charAt(Math.floor(Math.random() * characters.length));
				}
				return randomId;
			}

			function update(source: NodeEntity) {
				treeMap(root as unknown as d3.HierarchyNode<CourseNodeBase>);
				const treeData = root; // same object, now has x/y populated

				treeData.each((node) => {
					const id = generateRandomId();
					if (node.data.suppParent !== undefined) {
						node.data.suppParent = node.data.suppParent.map((parent) => {
							parent.id = `${parent.name?.replace(/\s/g, '')}-${node.depth - 1}`;
							return parent;
						});
					}
					node.id = id;
					node.data.id = id;
				});

				const multiParentNodes: MultiParentLink[] = [];
				const nodes = treeData.descendants();
				// Spacers stay in the link set on parent + admin so Bézier endpoints match visible spacer cards.
				const links = treeData
					.descendants()
					.filter((d) => !d.data.isArchived || (d.data.isArchived && d.data.children?.length > 0))
					.slice(1);

				nodes.forEach((d) => {
					if (d.data.isArchived && !d.set)
						d.descendants().forEach((desc) => {
							desc.depth -= 1;
						});
					if (!d.data.isArchived && d.set) {
						d.set = true;
						d.descendants().forEach((desc) => {
							desc.depth += 1;
						});
					}
					d.y = d.depth * 375;
				});

				// Sync React overlay state: coords, links, and multi-parent links are
				// all updated here in a single batch so the overlay re-renders once
				// with fully-consistent data (correct y values, correct node list).
				setCourseCoordsArray([...nodes]);
				setCourseSearchNodes(
					collectAllHierarchyNodes(root).filter((n) => !isSpacerMapNode(n.data.name))
				);
				setTreeLinks([...links]);

				// ── nodes ────────────────────────────────────────────────────
				const node = g
					.selectAll<SVGGElement, NodeEntity>('g.node')
					.data(nodes, (d) => d.data.tracker);

				const nodeEnter = node
					.enter()
					.append('g')
					.attr('class', (d) => (d.depth === 0 ? 'initial-node node' : 'node map-d3-node'))
					.attr('data-d3-tracker', (d) => d.data.tracker)
					.attr('id', (d, i) => `depth-${d.depth}-node-${i}`)
					.attr(
						'transform',
						() => `translate(${source.y0},${(source.x0 ?? 0) - rectNode.width / 4 - 6})`
					)
					.on('click', (_, d) => {
						if (d.depth === 1) {
							const { suppressNextDisciplineFitView, setSuppressNextDisciplineFitView } =
								useCourseMapStore.getState();
							if (!suppressNextDisciplineFitView) {
								shouldFitViewRef.current = true;
							} else {
								setSuppressNextDisciplineFitView(false);
							}
							click(d, update, isAdmin);
						}
					});

				// All visual card content (backdrop, rects, image, paths, text) is now
				// rendered by the <NodeCard> React component in the overlay SVG below.
				// The D3 <g> containers remain so D3 can still drive transitions and
				// the collapse/expand click handler.

				const nodeUpdate = nodeEnter.merge(node);

				nodeUpdate
					.lower()
					.conditionalTransition(isAdmin, duration)
					.attr(
						'transform',
						(d) => `translate(${(d as NodeEntity).y},${(d as NodeEntity).x - rectNode.height / 2})`
					)
					.attr('opacity', 1);

				node
					.exit()
					.lower()
					.conditionalTransition(isAdmin, duration)
					.attr(
						'transform',
						() => `translate(${source.y},${(source?.x ?? 0) - rectNode.width / 4 - 6})`
					)
					.attr('opacity', 0)
					.remove();

				// ── links ────────────────────────────────────────────────────
				// Build multiParentNodes from suppParent data, then hand both
				// link arrays to React state. TreeLinks / MultiParentLinks render
				// them in the overlay SVG. The auto-zoom that was in the old
				// linkEnter.on('start') callback now lives in a useEffect above.
				treeData.each((node) => {
					if (node.data.suppParent) {
						const parentIDArray = node.data.suppParent.map((parent) => parent.tracker);
						node.altConnections = [];
						parentIDArray.forEach((id: string) => {
							const match = findMatchingNode(treeData, id);
							if (match) node.altConnections!.push(match);
						});
						const ancestors = node.ancestors();
						const ancestorNode = ancestors[ancestors.length - 2]?.data;

						treeData.each((innerNode) => {
							if (parentIDArray.includes(innerNode.data.tracker)) {
								multiParentNodes.push({
									id: node.data.id,
									parent: { x: node.x, y: node.y + rectNode.width },
									y: innerNode.y + rectNode.width,
									x: innerNode.x,
									discipline:
										innerNode.parent?.data.discipline ??
										innerNode.data.discipline ??
										node.data.discipline,
									name: node.data.name,
									color: ancestorNode?.color,
								});
							}
						});
					}
				});

				setMultiParentLinks([...multiParentNodes]);

				nodes.forEach((d) => {
					d.x0 = d.x;
					d.y0 = d.y;
				});

				// ── edit icons (admin only) ──────────────────────────────────
				// All admin action icons have been migrated to React components
				// in the overlay SVG below:
				//   NodeAddRemoveButton — +/- circle + icon path
				//   NodeReorderControls — up/down circles + chevrons
				//   NodeMergeButton     — merge circle + M/R glyph
				// No D3 icon code remains here.
			}
		}

		if (isFetched) renderMap();
	}, [
		treeData,
		mapMode,
		isAdmin,
		passedEvent,
		updateTreeData,
		isFetched,
		rootNode,
		isLoading,
		findMatchingNode,
		setCourseCoordsArray,
		setCourseSearchNodes,
		duration,
		zoom,
		d3InstanceRef,
		disciplineData,
		siteSettings,
	]);

	// ── Popover screen-space positions ─────────────────────────────────────────
	// Converts a node's SVG coordinates into fixed screen coordinates so popovers
	// can be positioned next to their trigger button regardless of zoom/pan.
	const addPopoverPos = useMemo(() => {
		if (!addPopoverNode || !zoomTransform || !svgRef.current) return null;
		const svgRect = svgRef.current.getBoundingClientRect();
		return {
			x: zoomTransform.applyX(addPopoverNode.y + rectNode.extWidth) + svgRect.left + 12,
			y: zoomTransform.applyY(addPopoverNode.x - rectNode.height / 2) + svgRect.top,
		};
	}, [addPopoverNode, zoomTransform]);

	const mergePopoverPos = useMemo(() => {
		if (!mergePopoverNode || !zoomTransform || !svgRef.current) return null;
		const svgRect = svgRef.current.getBoundingClientRect();
		return {
			// Merge button is at the left edge of the card (node.y, node.x - height/2)
			x: zoomTransform.applyX(mergePopoverNode.y) + svgRect.left,
			y: zoomTransform.applyY(mergePopoverNode.x - rectNode.height / 2) + svgRect.top,
		};
	}, [mergePopoverNode, zoomTransform]);

	if (isLoading) {
		return (
			<div style={{ margin: 'auto', height: '100%' }}>
				<Loader color="var(--org-color)" ringColor="var(--org-color)" />
			</div>
		);
	}

	return (
		<>
			{/* ── ADMIN TUTORIAL ────────────────────────────────────────────── */}
			{isAdmin && (
				<AdminCourseMapIntro isEnabled={isTutorialEnabled} setIsEnabled={setIsTutorialEnabled} />
			)}

			{/* ── ADMIN TOOLBAR ─────────────────────────────────────────────── */}
			{isAdmin && (
				<StyledMapToolbar id="cm-toolbar">
					{/* Left group: panel toggle + mode segmented control */}
					<div className="flex items-center gap-3">
						<Button
							id="cm-course-panel-btn"
							size="small"
							variant={isMenuActive ? 'outline' : 'default'}
							onClick={() => {
								if (!isMenuActive) setSelectedNode(undefined);
								openPanel();
							}}
						>
							<PanelRight size={15} className="mr-1.5" />
							{isMenuActive ? 'Close Panel' : 'Course Panel'}
						</Button>
						<div className="flex items-center gap-2 border-l pl-3">
							<div
								id="cm-mode-segmented"
								className="inline-flex overflow-hidden rounded-lg border border-input"
							>
								{(
									[
										{ value: 'view', label: 'View' },
										{ value: 'edit', label: 'Edit' },
										{ value: 'remove', label: 'Remove' },
									] as const
								).map(({ value, label }, i) => (
									<button
										key={value}
										type="button"
										onClick={() => setMapMode(value)}
										className={cn(
											'px-3 py-1.5 text-sm font-medium select-none transition-colors',
											i > 0 && 'border-l border-input',
											mapMode === value
												? 'bg-purple-900 text-primary-foreground hover:bg-purple-700'
												: 'bg-background text-foreground hover:bg-accent hover:text-accent-foreground'
										)}
									>
										{label}
									</button>
								))}
							</div>
						</div>
					</div>

					{/* Right group: undo/redo + unsaved indicator + save */}
					<div className="flex items-center gap-2">
						{/* Undo / Redo */}
						<div id="cm-undo-redo" className="flex items-center gap-0.5">
							<button
								type="button"
								onClick={handleUndo}
								disabled={past.length === 0}
								title={
									past.length > 0
										? `Undo "${past[past.length - 1]?.label}" (⌘Z)`
										: 'Nothing to undo'
								}
								className="inline-flex items-center justify-center rounded-md p-1.5 text-sm transition-colors hover:bg-accent disabled:opacity-40 disabled:pointer-events-none"
							>
								<Undo2 size={15} />
							</button>
							<button
								type="button"
								onClick={handleRedo}
								disabled={future.length === 0}
								title={future.length > 0 ? `Redo "${future[0]?.label}" (⌘⇧Z)` : 'Nothing to redo'}
								className="inline-flex items-center justify-center rounded-md p-1.5 text-sm transition-colors hover:bg-accent disabled:opacity-40 disabled:pointer-events-none"
							>
								<Redo2 size={15} />
							</button>
						</div>

						{isMapAltered && (
							<StyledUnsavedButton
								id="cm-unsaved-btn"
								onClick={() => setIsHistoryDrawerOpen(true)}
								title="Review changes before saving"
							>
								<History size={14} />
								<span>
									{past.length} unsaved {past.length === 1 ? 'change' : 'changes'}
								</span>
							</StyledUnsavedButton>
						)}
						{isMapAltered && (
							<Button id="cm-save-btn" size="small" onClick={saveMapChanges}>
								Save Changes
							</Button>
						)}

						{/* Help / tutorial trigger */}
						<button
							id="cm-help-btn"
							type="button"
							onClick={() => {
								try {
									localStorage.setItem('elevateAdminCourseMapTutorial', 'true');
								} catch {
									/* noop */
								}
								setIsTutorialEnabled(true);
							}}
							title="Show tutorial"
							className="inline-flex items-center justify-center rounded-md p-1.5 text-sm transition-colors hover:bg-accent text-muted-foreground"
						>
							<HelpCircle size={15} />
						</button>
					</div>
				</StyledMapToolbar>
			)}

			{/* position:relative makes the overlay SVG anchor to this container */}
			<StyledMap
				id="course-map"
				ref={svgChartRef}
				style={{
					position: 'relative',
					height: isAdmin ? 'calc(100vh - 92px - 52px)' : undefined,
				}}
			>
				{!isAdmin && (
					<MapButtonControls
						zoom={zoom}
						svgInstance={d3InstanceRef}
						duration={duration / 2}
						zoomRef={zoomRef}
					/>
				)}

				{/* D3-managed SVG — unchanged from CourseMap */}
				<svg ref={svgRef} />

				{/* ── REACT SVG OVERLAY ──────────────────────────────────────────
			    This is the React rendering layer. It sits pixel-perfect over
			    the D3 SVG by applying the same zoom transform to its inner <g>.
			    `pointerEvents: none` on the outer SVG passes mouse events through
			    to D3 for zoom/pan; `pointerEvents: auto` on individual elements
			    re-enables interaction for those specific components.
			    Migrated so far:
			      1. ViewButton          — "View" button on course nodes (depth > 1)
			      2. NodeAddRemoveButton — +/- circle & icon on every node
			      3. NodeReorderControls — up/down circles & chevrons on discipline nodes
			      4. NodeMergeButton     — merge circle & M/R glyph on every node
			      5. NodeCard            — entire card body (rects, image, paths, text)
			      6. TreeLinks           — curved Bézier connectors between nodes
			      7. MultiParentLinks    — supplemental-parent connectors
			    ─────────────────────────────────────────────────────────────── */}
				{zoomTransform && (
					<svg
						style={{
							position: 'absolute',
							inset: 0,
							width: '100%',
							height: '100%',
							overflow: 'visible',
							pointerEvents: 'none',
						}}
					>
						{/* clipPath for image corners — must live in this SVG since
				    SVG clipPath IDs are scoped to their parent SVG document */}
						<defs>
							<clipPath id="react-round-corner">
								<rect x={0} y={0} width={rectNode.extWidth} height={rectNode.height} rx={6} />
							</clipPath>
						</defs>

						<g transform={zoomTransform.toString()}>
							{/* Migrations 6 & 7 – Links rendered FIRST so they sit behind cards in
					    SVG paint order (equivalent to D3's .lower() calls) */}
							<MultiParentLinks links={multiParentLinks} />
							<TreeLinks links={treeLinks} />

							{(courseCoordsArray as NodeEntity[] | undefined)
								?.filter((node) => !node.data.isArchived)
								.map((node) => (
									<g
										key={`${node.parent?.data.tracker ?? 'root'}-${node.data.tracker}-${node.depth}`}
										opacity={
											dragState && descendantTrackersRef.current.has(node.data.tracker) ? 0.2 : 1
										}
									>
										{/* Migration 5 – Course cards; spacers use dashed NodeCard (admin: +/−/merge; parent: non-interactive) */}
										<NodeCard
											isAdmin={isAdmin}
											node={node}
											disciplineData={disciplineData}
											siteSettings={siteSettings}
											enterFrom={
												enterSourceRef.current &&
												!prevNodeTrackersRef.current.has(node.data.tracker)
													? enterSourceRef.current
													: undefined
											}
											onClick={
												!isAdmin && node.depth === 1
													? () => {
															if (d3UpdateRef.current) {
																enterSourceRef.current = {
																	x: node.y,
																	y: node.x - rectNode.height / 2,
																};
																shouldFitViewRef.current = true;
																click(node, d3UpdateRef.current, isAdmin);
															}
														}
													: undefined
											}
											onPointerDown={
												isAdmin && mapMode === 'edit' ? handlePointerDown(node) : undefined
											}
											onPointerMove={
												isAdmin && mapMode === 'edit' ? handlePointerMove(node) : undefined
											}
											onPointerUp={
												isAdmin && mapMode === 'edit' ? handlePointerUp(node) : undefined
											}
										/>

										{/* Migration 1 – View button (real courses on parent map only; not spacers) */}
										{node.depth > 1 && !isAdmin && !isSpacerMapNode(node.data.name) && (
											<ViewButton node={node} passedEvent={passedEvent} />
										)}

										{/* Migration 2 – Add/remove circle + icon (hidden in View mode) */}
										{isAdmin && mapMode !== 'view' && (
											<NodeAddRemoveButton
												node={node}
												isAddingCourse={isAddingCourse}
												activeTrackerId={activeTrackerId}
												onAddClick={handleNodeAddClick}
												onRemoveClick={handleNodeRemoveClick}
											/>
										)}

										{/* Migration 3 – Reorder up/down (discipline nodes, admin only) */}
										{isAdmin && node.depth === 1 && (
											<NodeReorderControls
												node={node}
												onMoveUp={handleMoveUp}
												onMoveDown={handleMoveDown}
											/>
										)}

										{/* Migration 4 – Merge circle + M/R glyph (hidden in View mode; spacers are layout-only) */}
										{isAdmin && mapMode !== 'view' && !isSpacerMapNode(node.data.name) && (
											<NodeMergeButton
												node={node}
												isAddingCourse={isAddingCourse}
												onMergeClick={handleMergeClick}
											/>
										)}
									</g>
								))}

							{/* Drop highlight — rendered AFTER all cards so it's never obscured */}
							{dropTarget && <DropHighlight node={dropTarget} />}

							{/* Drag ghost — rendered on top of everything while dragging */}
							{dragState && (
								<DragGhost
									x={dragState.x}
									y={dragState.y}
									node={dragState.node}
									dropTargetName={dropTarget?.data.name ?? null}
								/>
							)}
						</g>
					</svg>
				)}

				{/* Drag-cancel hint — fixed at bottom-center, unaffected by zoom/pan */}
				{dragState && (
					<div className="fixed left-1/2 transform -translate-x-1/2 bg-black/65 text-white px-4 py-2 rounded-md text-sm font-semibold backdrop-blur-4px pointer-events-none select-none whitespace-nowrap">
						Press{' '}
						<kbd className="bg-white/20 border border-white/35 rounded-md px-1.5 py-0.5 text-sm font-semibold">
							Esc
						</kbd>{' '}
						to cancel
					</div>
				)}
			</StyledMap>

			{/* ── Add-course popover ─────────────────────────────────────────── */}
			{addPopoverNode && addPopoverPos && (
				<AddCoursePopover
					node={addPopoverNode}
					screenX={addPopoverPos.x}
					screenY={addPopoverPos.y}
					selectedNode={selectedNode}
					newCourse={newCourse}
					treeData={treeData}
					updateTreeData={updateTreeData}
					onClose={() => {
						setAddPopoverNode(null);
						setActiveTrackerId(null);
					}}
				/>
			)}

			{/* ── Merge popover ──────────────────────────────────────────────── */}
			{mergePopoverNode && mergePopoverPos && (
				<MergePopover
					node={mergePopoverNode}
					screenX={mergePopoverPos.x}
					screenY={mergePopoverPos.y}
					isAddingCourse={isAddingCourse}
					updateTreeData={updateTreeData}
					onClose={() => setMergePopoverNode(null)}
				/>
			)}

			<ToastNotification />
			{isConfirming && (
				<ConfirmModal
					modalActive={isConfirming}
					onConfirm={removeCourse}
					onCancel={() => setIsConfirming(false)}
					triggerModal={() => setIsConfirming(false)}
					message="Are you sure you want to delete this course?"
					selectedData={currentCourse?.course_name ?? ''}
				/>
			)}
			{isAdmin && (
				<Drawer
					direction="right"
					open={isMenuActive}
					onOpenChange={(open) => {
						if (!open) {
							setIsMenuActive(false);
							setActiveTrackerId(null);
						}
					}}
					shouldScaleBackground={false}
				>
					<DrawerContent className="top-0 right-0 mt-0 h-full w-[400px] shadow-[-4px_0px_8px_rgb(0_0_0/0.15)] bg-(--panel-color)">
						<DrawerTitle className="sr-only">Course Panel</DrawerTitle>
						<CourseMapList
							deleteCourse={confirmDelete}
							checkTreeDiff={checkTreeDiff}
							treeData={treeData}
							selectedNode={selectedNode}
							newCourse={newCourse}
							updateTreeData={updateTreeData}
						/>
					</DrawerContent>
				</Drawer>
			)}

			{/* ── Change-history review drawer ─────────────────────────────────── */}
			<Drawer
				direction="bottom"
				open={isHistoryDrawerOpen}
				onOpenChange={setIsHistoryDrawerOpen}
				shouldScaleBackground={false}
			>
				<DrawerContent className="bottom-0 inset-x-0 mx-auto max-w-2xl rounded-t-xl max-h-[60vh]">
					<DrawerHeader className="border-b pb-4">
						<DrawerTitle className="flex items-center gap-2 text-sm">
							Review Changes
							<span className="ml-auto text-sm font-normal text-muted-foreground">
								{past.length} {past.length === 1 ? 'change' : 'changes'}
							</span>
						</DrawerTitle>
					</DrawerHeader>

					{/* Timeline */}
					<div className="flex-1 overflow-y-auto px-4 py-3">
						{past.length === 0 ? (
							<p className="py-8 text-center text-sm text-muted-foreground">
								No changes to review.
							</p>
						) : (
							<ol className="relative border-l border-border ml-3">
								{[...past].reverse().map((entry: HistoryEntry) => (
									<li key={entry.timestamp.toISOString() + entry.label} className="mb-4 ml-5">
										{/* Timeline dot */}
										<span className="absolute -left-[9px] flex h-[18px] w-[18px] items-center justify-center rounded-full bg-background ring-2 ring-border">
											<span className="h-2 w-2 rounded-full bg-primary" />
										</span>
										<p className="text-sm font-medium leading-snug pl-4">{entry.label}</p>
										<time className="mt-0.5 block text-xs text-muted-foreground pl-4">
											{formatRelativeTime(entry.timestamp)}
										</time>
									</li>
								))}
							</ol>
						)}
					</div>

					<DrawerFooter className="border-t pt-3">
						<Button onClick={commitSave} disabled={past.length === 0}>
							Confirm &amp; Save
						</Button>
						<Button variant="outline" onClick={() => setIsHistoryDrawerOpen(false)}>
							Keep Editing
						</Button>
					</DrawerFooter>
				</DrawerContent>
			</Drawer>
		</>
	);
};

export default CourseMapD3React;
